import type { App } from "@upsquad/server";
import type { SendInvitationsPayload } from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

type GetInvitationsResponse = App["~Routes"]["invitation"][":communityId"]["get"]["response"]["200"];
type SendInvitationsResponse = App["~Routes"]["invitation"][":communityId"]["post"]["response"]["200"];
type CancelInvitationResponse = App["~Routes"]["invitation"][":communityId"][":invitationId"]["delete"]["response"]["200"];
type ResendInvitationResponse = App["~Routes"]["invitation"][":communityId"][":invitationId"]["resend"]["post"]["response"]["200"];
type BulkRemindInvitationsResponse = App["~Routes"]["invitation"][":communityId"]["reminders"]["bulk"]["post"]["response"]["200"];

interface GetInvitationsParams {
	communityId: string;
	page?: number;
	limit?: number;
}

interface SendInvitationsParams {
	communityId: string;
	payload: SendInvitationsPayload;
}

interface CancelInvitationParams {
	communityId: string;
	invitationId: string;
}

interface ResendInvitationParams {
	communityId: string;
	invitationId: string;
}

interface BulkRemindInvitationsParams {
	communityId: string;
	note?: string;
}

const memberInvitationsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getInvitations: builder.query<GetInvitationsResponse, GetInvitationsParams>({
			query:
				({ communityId, page, limit }): EdenCall<GetInvitationsResponse> =>
				(client) =>
					client.invitation({ communityId }).get({
						query: { page: page ?? 1, limit: limit ?? 10 },
					}),
			providesTags: ["Invitations"],
		}),
		sendInvitations: builder.mutation<SendInvitationsResponse, SendInvitationsParams>({
			query:
				({ communityId, payload }) =>
				(client) =>
					client.invitation({ communityId }).post(payload),
			invalidatesTags: ["Invitations", "Members"],
		}),
		cancelInvitation: builder.mutation<CancelInvitationResponse, CancelInvitationParams>({
			query:
				({ communityId, invitationId }) =>
				(client) =>
					client.invitation({ communityId })({ invitationId }).delete(),
			invalidatesTags: ["Invitations"],
		}),
		resendInvitation: builder.mutation<ResendInvitationResponse, ResendInvitationParams>({
			query:
				({ communityId, invitationId }) =>
				(client) =>
					client.invitation({ communityId })({ invitationId }).resend.post(),
			invalidatesTags: ["Invitations"],
		}),
		bulkRemindInvitations: builder.mutation<BulkRemindInvitationsResponse, BulkRemindInvitationsParams>({
			query:
				({ communityId, note }): EdenCall<BulkRemindInvitationsResponse> =>
				(client) =>
					client.invitation({ communityId }).reminders.bulk.post({ note }),
			invalidatesTags: ["Invitations"],
		}),
	}),
});

export const {
	useGetInvitationsQuery,
	useSendInvitationsMutation,
	useCancelInvitationMutation,
	useResendInvitationMutation,
	useBulkRemindInvitationsMutation,
} = memberInvitationsApi;
